import { Accordion, Col, Form, Row, Table } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { FaDesktop, FaMobileScreenButton } from 'react-icons/fa6';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import URLRenderer from '@/app/common/components/CellRenderers/URLRenderer';
import Url from '@/app/common/components/Url';
import ChildrensTooltip from '@/components/common/ChildrensTooltip';
import AgGridWrapper from '@/app/common/components/ag-grid/AgGridWrapper';
import DrawerWrapper from '@/app/common/components/DrawerWrapper';
import type { ComponentType, ReactElement, ReactNode } from 'react';
import SubtleBadge from '@/components/common/SubtleBadge';
import type {
  AnalysisCookie,
  AnalysisHeaderPair,
  AnalysisSettings
} from '@/app/PageSpeed/api/types/analysisApiTypes';
import type { ColumnMenuTab } from 'ag-grid-community';

const menuTabs: ColumnMenuTab[] = [
  'columnsMenuTab',
  'filterMenuTab',
  'generalMenuTab'
];

const SimpleURLRenderer = URLRenderer as (props: {
  url?: string | null;
}) => ReactElement;

type DrawerWrapperProps = {
  title: ReactNode;
  children: ReactNode;
  show: boolean;
  onHide?: () => void;
  width?: number;
};
const TypedDrawerWrapper = DrawerWrapper as ComponentType<DrawerWrapperProps>;

type AgGridWrapperProps = {
  height?: string;
  columns?: unknown[];
  data?: unknown[];
  settings?: Record<string, unknown>;
};
const TypedAgGridWrapper = AgGridWrapper as ComponentType<AgGridWrapperProps>;

type SettingsDrawerPayload = AnalysisSettings;

type DrawerState = {
  drawer: {
    key?: string;
    payload?: SettingsDrawerPayload | null;
  };
};

const publicReportUrl = (slug?: string | null) =>
  slug
    ? `${import.meta.env.VITE_REACT_APP_MIZFA_TOOLS_PANEL_URL}/reporting/page_speed/${slug}`
    : '';

const AnalysisSettingsDrawer = () => {
  const { key, payload } = useSelector((state: DrawerState) => state.drawer);
  const {
    url,
    device_type,
    location_code,
    internet_type,
    user_agent,
    headers,
    cookies,
    is_public,
    slug
  } = payload ?? {};
  const reportUrl = is_public ? publicReportUrl(slug) : '';

  return (
    <TypedDrawerWrapper
      show={key === 'analysis_settings'}
      width={650}
      title={
        <div className="d-flex flex-wrap gap-3 align-items-center">
          <h6 className="m-0 d-flex gap-1">
            تنظیمات واکاوی
            <strong>{url ? <SimpleURLRenderer url={url} /> : ''}</strong>
          </h6>
        </div>
      }
    >
      <Row>
        <Col>
          <div id="overview" className="mt-5">
            <Accordion defaultActiveKey={['0']} alwaysOpen>
              <Accordion.Item eventKey="0">
                <Accordion.Header>تنظیمات کلی</Accordion.Header>
                <Accordion.Body>
                  <Table size="sm">
                    <tbody>
                      <tr>
                        <td>نشانی</td>
                        <td>
                          <Url url={url} />
                        </td>
                      </tr>
                      <tr>
                        <td>نوع گزارش</td>
                        <td>
                          {reportUrl ? (
                            <a
                              href={reportUrl}
                              target="_blank"
                              rel="noreferrer"
                              className="text-decoration-none"
                            >
                              <SubtleBadge bg="info">عمومی</SubtleBadge>
                            </a>
                          ) : (
                            <SubtleBadge bg={is_public ? 'info' : 'danger'}>
                              {is_public ? 'عمومی' : 'خصوصی'}
                            </SubtleBadge>
                          )}
                        </td>
                      </tr>
                      <tr>
                        <td>نوع دستگاه</td>
                        <td>
                          <div className="d-flex align-items-center gap-2">
                            <ChildrensTooltip
                              placement="top"
                              delayToRemove={0}
                              html={
                                (device_type as unknown) === 1
                                  ? 'رایانه'
                                  : 'گوشی'
                              }
                            >
                              {(device_type as unknown) === 1 ? (
                                <FaDesktop title="دکستاپ" />
                              ) : (
                                <FaMobileScreenButton title="موبایل" />
                              )}
                            </ChildrensTooltip>
                            {(device_type as unknown) === 1
                              ? 'رایانه'
                              : 'گوشی'}
                          </div>
                        </td>
                      </tr>

                      <tr>
                        <td>سرور بررسی</td>
                        <td>
                          <div className="d-flex align-items-center gap-2">
                            <ChildrensTooltip
                              placement="top"
                              delayToRemove={0}
                              html={
                                location_code === 'not_ir'
                                  ? 'خارج'
                                  : location_code === 'google'
                                    ? 'گوگل'
                                    : location_code
                              }
                            >
                              {location_code === 'not_ir' ? (
                                <FontAwesomeIcon icon="globe" />
                              ) : location_code === 'google' ? (
                                <FontAwesomeIcon icon={['fab', 'google']} />
                              ) : (
                                <img
                                  src={`https://static.mizfa.tools/flags/${location_code}.webp`}
                                />
                              )}
                            </ChildrensTooltip>
                            <span>
                              {location_code === 'not_ir'
                                ? 'خارج'
                                : location_code === 'google'
                                  ? 'گوگل'
                                  : location_code === 'ir'
                                    ? 'ایران'
                                    : location_code}
                            </span>
                          </div>
                        </td>
                      </tr>
                      <tr>
                        <td>سرعت اینترنت</td>
                        <td>{internet_type || 'نامشخص'}</td>
                      </tr>
                    </tbody>
                  </Table>
                  {user_agent && (
                    <div className="mx-3 mt-n3">
                      <td className="fs--1 text-800">User Agent</td>
                      <Form.Control
                        disabled
                        dir="ltr"
                        className="w-100 mt-1 mb-3"
                        as="textarea"
                        rows={3}
                        value={user_agent}
                      />
                    </div>
                  )}
                </Accordion.Body>
              </Accordion.Item>
              {(headers?.length ?? 0) > 0 && (
                <Accordion.Item eventKey="1">
                  <Accordion.Header>اطلاعات هدر</Accordion.Header>
                  <Accordion.Body>
                    <Table size="sm">
                      <tbody>
                        {headers?.map((item: AnalysisHeaderPair, index: number) => (
                          <tr key={index}>
                            <td>{item.name}</td>
                            <td>{item.value}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </Accordion.Body>
                </Accordion.Item>
              )}
              {(cookies?.length ?? 0) > 0 && (
                <Accordion.Item eventKey="2">
                  <Accordion.Header>اطلاعات کوکی‌ها </Accordion.Header>
                  <Accordion.Body className="p-0">
                    <TypedAgGridWrapper
                      settings={{
                        pagination: false,
                        fullscreen: false,
                        changeHeight: false,
                        resetGrid: false,
                        saveGrid: false
                      }}
                      columns={[
                        {
                          field: 'name',
                          type: 'string',
                          title: 'name',
                          width: 100,
                          searchable: true,
                          filter: 'agTextColumnFilter',
                          floatingFilter: true,
                          menuTabs
                        },
                        {
                          field: 'value',
                          type: 'string',
                          title: 'value',
                          width: 100,
                          searchable: true,
                          filter: 'agTextColumnFilter',
                          floatingFilter: true,
                          menuTabs
                        },
                        {
                          field: 'domain',
                          type: 'string',
                          title: 'domain',
                          width: 120,
                          searchable: true,
                          filter: 'agTextColumnFilter',
                          floatingFilter: true,
                          menuTabs
                        },
                        {
                          field: 'path',
                          type: 'string',
                          title: 'path',
                          width: 120,
                          searchable: true,
                          filter: 'agTextColumnFilter',
                          floatingFilter: true,
                          menuTabs
                        },
                        {
                          field: 'secure',
                          type: 'string',
                          title: 'secure',
                          width: 100,
                          searchable: true,
                          filter: 'agTextColumnFilter',
                          floatingFilter: true,
                          menuTabs,
                          cellRenderer: (params: { value?: boolean }) =>
                            params.value ? 'true' : 'false'
                        },
                        {
                          field: 'httpOnly',
                          type: 'string',
                          title: 'httpOnly',
                          width: 100,
                          searchable: true,
                          filter: 'agTextColumnFilter',
                          floatingFilter: true,
                          menuTabs,
                          cellRenderer: (params: { value?: boolean }) =>
                            params.value ? 'true' : 'false'
                        }
                      ]}
                      data={(cookies || []) as AnalysisCookie[]}
                      height="400px"
                    />
                  </Accordion.Body>
                </Accordion.Item>
              )}
            </Accordion>
            <style>
              {`
          .table {
            width: auto !important;
            font-size: 14px;
          }

          .table > :not(caption) > * > *{
            color:var(--falcon-gray-800) !important;
          }


          .accordion-button:not(.collapsed),.accordion-button{
            color:var(--falcon-gray-900) !important;
            font-weight: 500;
          }

          thead, tbody, tfoot, tr, td, th {
            border-style: hidden;
          }

          ul{
            list-style-type: auto;
          }
          
          .table tfoot > tr > th:last-child, .table thead > tr > th:last-child, .table tr th:last-child, .table tr td:last-child {
            padding-right: 1.25rem;
          }
        `}
            </style>
          </div>
        </Col>
      </Row>
    </TypedDrawerWrapper>
  );
};

export default AnalysisSettingsDrawer;
